import React, { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useUser } from "@/base/components/auth/UserProvider";
import { useNotification } from "@/base/components/NotificationProvider";
import Layout from "@/components/layout/main";
import DashboardLayout from "@/base/components/layouts/dashboard";
import DashboardLayoutEntry from "@/base/components/dashboard/DashboardLayoutEntry";
import SimpleCard from "@/base/components/dashboard/SimpleCart";
import { ModifyURL } from "@/base/utils/fetch";
import {
  DataPageOutput,
  getDefaultPageOutput,
  PageOutput,
} from "@/base/types/Pagination";
import PagedItems, {
  OnPageChange,
} from "@/base/components/dashboard/PagedItems";
import { Button, Input, InputRef, Tooltip } from "antd";
import { PromoCode } from "@/base/types/PromoCode";
import { ClearQueryParams } from "@/base/utils/RouterUtils";
import MetaBlock from "@/base/components/MetaBlock";
import { IoBagCheckOutline } from "react-icons/io5";
import { AvailablePromoCode } from "@/base/types/AvailablePromoCode";
import { IoIosMail, IoIosPhonePortrait, IoMdPricetag } from "react-icons/io";
import { MdOutlineRateReview } from "react-icons/md";
import { IconBaseProps } from "react-icons";
import { FaSearchDollar } from "react-icons/fa";
import TopUpReward from "@/base/types/Cart/TopUpReward";
import { LoadingItem } from "@/base/types/LoadingItem";
import { DelayedEffectHook } from "@/base/types/delayed-effect.hook";
import { Loading } from "@/base/components/Loading";
import { LuHandHeart } from "react-icons/lu";
import NewsBoard from "@/base/components/dashboard/NewsBoard";
import Image from "next/image";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Link from "next/link";
import { getRefValue, setRefValue } from "@/base/utils/form";
import { useTheme } from "@/base/components/theme/BaseThemeProvider";
import { AvailablePromoCard } from "@/base/components/dashboard/AvailablePromoCard";
import { TopupBonus } from "@/base/components/dashboard/TopupBonus";

const icons: Record<string, (props: IconBaseProps) => ReactNode> = {
  first_order: (props: IconBaseProps) => <IoBagCheckOutline {...props} />,
  phone_link: (props: IconBaseProps) => <IoIosPhonePortrait {...props} />,
  email_link: (props: IconBaseProps) => <IoIosMail {...props} />,
  leave_review: (props: IconBaseProps) => <MdOutlineRateReview {...props} />,
};

const TopUpBonusesList = ({
  className,
  bonuses,
}: {
  className?: string;
  bonuses: TopUpReward[];
}) => {
  let lastFrom = 0;
  let wasProgress = false;

  return (
    <>
      {bonuses.map((bonus, i) => {
        const to = bonus.from;

        const el = (
          <TopupBonus
            className={"px-4 pt-6 pb-5"}
            animate={true}
            bonus={bonus}
            i={i}
            to={to}
            lastFrom={lastFrom}
            wasProgress={wasProgress}
          />
        );

        if (bonus.percentage < 0.98 && !wasProgress) {
          wasProgress = true;
        }
        lastFrom = bonus.from;

        return el;
      })}
    </>
  );
};

const ActivatePromoWidget = ({
  className,
  onActivate,
}: {
  className?: string;
  onActivate?: (code: string) => void;
}) => {
  const { t } = useTranslation();

  const [api] = useNotification();

  const UserContext = useUser();
  const router = useRouter();
  const { theme } = useTheme();

  const ref = React.useRef<InputRef>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [allowed, setAllowed] = React.useState<boolean>(false);

  const handleOk = async (code?: string) => {
    setLoading(true);
    code = code || getRefValue(ref) || "";

    return UserContext.Fetch({
      url: ModifyURL(`/promo/activate`),
      method: "POST",
      body: {
        code,
      },
    })
      .then((res) => {
        if (!res?.data?.status) {
          throw new Error(JSON.stringify(res));
        }

        api.success({
          message: t("base-dashboard:promo.activate-modal.success"),
        });

        if (onActivate && code) {
          onActivate(code);
        }
      })
      .catch((err) => {
        console.error(err);

        api.error({
          message: err.message || err,
        });
      })
      .finally(() => setLoading(false));
  };

  const validateCode = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // bigger than 5
    setAllowed(e.target.value.length >= 1);
  };

  useEffect(() => {
    if (router.query["activatepromo"]) {
      const code = router.query["activatepromo"] as string;
      ClearQueryParams(["activatepromo"], router);

      setTimeout(() => {
        setRefValue(ref, code);
        handleOk(code);
      }, 300);
    }
  }, [router.query["activatepromo"]]);

  return (
    <div
      className={`flex bg-primary-100 promo-pl rounded-xl hover:!bg-primary-200/80 duration-150 group !overflow-hidden ${className}`}
    >
      <Input
        type="email"
        defaultValue={``}
        bordered={false}
        rootClassName={`!bg-transparent`}
        className={`!border-none !rounded-none !bg-transparent hover:!bg-transparent`}
        placeholder={`SP1-XXXX-XXXX`}
        ref={ref}
        onChange={validateCode}
      />
      <Button
        type={"primary"}
        className={`!shadow-none max-lg:!w-full !font-extrabold max-w-[300px] !h-auto !py-2 m-1 ${
          !allowed && `opacity-80 saturate-50 pointer-events-none`
        }`}
        onClick={() => handleOk()}
      >
        {t("base-dashboard-promo:custom-code.activate")}
      </Button>
    </div>
  );
};

export default function Page() {
  const { t } = useTranslation();
  const UserContext = useUser();
  const [api] = useNotification();

  const [refreshPromosState, setRefreshPromosState] = useState<number>(-1);

  const activePromoListState = useState<PageOutput<PromoCode>>(
    getDefaultPageOutput(5)
  );

  const loadActivePromoList: OnPageChange<PromoCode> = async (
    page,
    perPage,
    setList,
    list
  ) => {
    if (!UserContext.loggedIn) {
      return;
    }

    return UserContext.Fetch({
      url: ModifyURL(`/promo/list`),
      params: { page, perPage },
    })
      .then((res) => {
        setList(
          DataPageOutput(
            res?.data || [],
            (item: any) => item as PromoCode,
            1000
          )
        );
      })
      .catch((err) => {
        api.error({
          message: err.message || err,
        });
      });
  };

  const [TopUpBonuses, setTopUpBonuses] = useState<LoadingItem<TopUpReward[]>>({
    item: [],
    loading: true,
  });
  const loadRewardBOnuses = async () => {
    if (!UserContext.loggedIn) {
      return;
    }

    setTopUpBonuses({
      ...TopUpBonuses,
      loading: true,
    });

    return UserContext.Fetch({
      url: ModifyURL(`/promo/topup-rewards`),
      params: {},
    })
      .then((res: { data: TopUpReward[] }) => {
        setTopUpBonuses({
          item: res?.data || [],
          loading: false,
        });
      })
      .catch((err) => {
        api.error({
          message: err.message || err,
        });

        setTopUpBonuses({
          ...TopUpBonuses,
          loading: false,
        });
      });
  };

  DelayedEffectHook(
    () => {
      loadRewardBOnuses();
    },
    [refreshPromosState, UserContext.user],
    200
  );

  DelayedEffectHook(
    () => {
      const canActivateBonus = TopUpBonuses.item.find((v) => v.can_take);
      if (canActivateBonus) {
        activateTOpUpBonus(canActivateBonus).then(() => {
          console.log("Activated bonus", canActivateBonus);
        });
      }
    },
    [TopUpBonuses],
    200
  );

  const availablePromoState = useState<PageOutput<AvailablePromoCode>>(
    getDefaultPageOutput(5)
  );
  const loadAvailablePromos: OnPageChange<AvailablePromoCode> = async (
    page,
    perPage,
    setList,
    list
  ) => {
    if (!UserContext.loggedIn) {
      return;
    }

    return UserContext.Fetch({
      url: ModifyURL(`/promo/available`),
      params: {},
    })
      .then((res) => {
        setList(
          DataPageOutput(
            res?.data || [],
            (item: any) => item as AvailablePromoCode,
            100
          )
        );
      })
      .catch((err) => {
        api.error({
          message: err.message || err,
        });
      });
  };

  const [loadingActivateTOpUpBonus, setLoadingActivateTOpUpBonus] =
    useState<boolean>(false);

  const activateTOpUpBonus = async (promo: TopUpReward) => {
    setLoadingActivateTOpUpBonus(true);

    return UserContext.Fetch({
      url: ModifyURL(`/promo/topup-rewards/${promo.id}`),
      method: "PUT",
    })
      .then(() => {
        setRefreshPromosState(Math.random());
        setTimeout(() => {
          UserContext.updateUser(true);
        }, 300);
      })
      .catch((err) => {
        api.error({
          message: err.message || err,
        });
      })
      .finally(() => setLoadingActivateTOpUpBonus(false));
  };

  return (
    <DashboardLayoutEntry.Base>
      <DashboardLayoutEntry.Content>
        <div className="grid grid-cols-1 gap-4 lg:gap-8 max-md:p-2">
          <Loading
            show={TopUpBonuses.loading || loadingActivateTOpUpBonus}
            innerClassName={`grid grid-cols-1 lg:grid-cols-5 rounded-2xl overflow-hidden shadow-[0px_5px_4px_3px_rgba(233,235,244,0.8)] dark:shadow-[0px_5px_4px_3px_rgba(133,135,144,0.4)]`}
          >
            <TopUpBonusesList className={``} bonuses={TopUpBonuses.item} />
          </Loading>
          <div className={`grid xl:grid-cols-7 gap-6`}>
            <div className={`gap-6 flex flex-col xl:col-span-3`}>
              <div className={`grid gap-4 h-full`}>
                <SimpleCard
                  className={`rounded-2x shadow-[0px_4px_3px_2px_rgba(233,235,244,0.5)] border-solid border-gray-400/30 border-[0.5px] flex-auto`}
                  bodyClassName={`gap-6 flex flex-col relative overflow-hidden h-full !p-4 !px-6`}
                  headerClassName={"!pb-6"}
                  removePaddings={true}
                >
                  <div className={`flex flex-col basis-[fit-content] gap-6`}>
                    <div
                      className={`w-[60px] h-[60px] flex-none rounded-xl border-primary border-solid border-[3px] bg-primary p-3`}
                    >
                      <IoMdPricetag size={"100%"} className={`text-white`} />
                    </div>
                  </div>
                  <Image
                    src={`/base/image/wallet-icon.svg`}
                    alt={t("base-dashboard-promo:topup-rewards.title")}
                    width={140}
                    height={140}
                    className={`absolute right-0 top-0 mt-4 mr-4 dark:hidden`}
                  />
                  <div className={`flex flex-col gap-2.5 relative`}>
                    <h3
                      className={`font-montserrat text-text-2 text-2xl font-semibold`}
                    >
                      {t("base-dashboard-promo:list.card.title")}
                    </h3>
                    <PagedItems<PromoCode>
                      dependencies={[UserContext.user, refreshPromosState]}
                      onChange={loadActivePromoList}
                      state={activePromoListState}
                      bodyClassName={`space-y-4 h-full`}
                      className={`h-full`}
                      rootClassName={`h-full`}
                      pageProps={{
                        showSizeChanger: false,
                      }}
                      emptyItem={() => (
                        <div className={`h-full flex flex-col justify-center`}>
                          <div
                            className={`text-start text-gray-400 gap-4 flex items-center`}
                          >
                            <FaSearchDollar
                              size={`40px`}
                              className={`text-text-2`}
                            />
                            <div className={`flex flex-col gap-2.5`}>
                              <p className={`text-text-2 text-lg`}>
                                {t("base-dashboard-promo:list.card.empty")}
                              </p>
                              <p className={`max-w-[500px]`}>
                                {t(
                                  "base-dashboard-promo:list.card.empty-description"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      renderItem={(promo, k) => (
                        <div key={k} className={`flex flex-wrap gap-4`}>
                          {/*<div className={`w-[30px] h-[30px] rounded-full max-md:hidden border-primary border-solid border-[3px] ${k == 0 && `bg-primary`}`}/>*/}
                          <div className={`space-y-2`}>
                            <Tooltip title={promo?.data?.description}>
                              <p
                                className={`text-color flex items-center gap-2`}
                              >
                                <span className={`!text-[10px]`}>●</span>
                                <span>
                                  {promo?.data?.name}
                                  {promo?.usages > 0 && (
                                    <span className={``}>
                                      ,{" "}
                                      {t(
                                        "base-dashboard-promo:list.card.promo-count"
                                      )}{" "}
                                      {promo?.usages}
                                    </span>
                                  )}
                                </span>
                                {promo?.data?.description && (
                                  <AiOutlineInfoCircle />
                                )}
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </SimpleCard>
              </div>
            </div>

            <div className={`gap-6 flex flex-col xl:col-span-2`}>
              <div className={`grid gap-4 h-full`}>
                <SimpleCard
                  className={`rounded-2x shadow-[0px_4px_3px_2px_rgba(233,235,244,0.5)] border-solid border-gray-400/30 border-[0.5px] flex-auto`}
                  bodyClassName={`gap-6 flex flex-col relative overflow-hidden h-full !p-4`}
                  headerClassName={"!pb-6"}
                  removePaddings={true}
                >
                  <div className={`flex flex-col basis-[fit-content] gap-6`}>
                    <div
                      className={`w-[60px] h-[60px] flex-none rounded-xl border-primary border-solid border-[3px] bg-primary p-3`}
                    >
                      <IoMdPricetag size={"100%"} className={`text-white`} />
                    </div>
                  </div>
                  <div className={`flex flex-col gap-2.5 my-auto`}>
                    <h3
                      className={`font-montserrat text-text-2 text-2xl font-semibold`}
                    >
                      {t("base-dashboard-promo:custom-code.title")}
                    </h3>

                    <p>{t("base-dashboard-promo:custom-code.description")}</p>
                  </div>
                  <ActivatePromoWidget
                    onActivate={(code: string) =>
                      setRefreshPromosState(Math.random())
                    }
                  />
                </SimpleCard>
              </div>
            </div>

            <div className={`gap-6 flex flex-col xl:col-span-2`}>
              <div className={`grid gap-4 h-full`}>
                <SimpleCard
                  className={`rounded-2x shadow-[0px_4px_3px_2px_rgba(233,235,244,0.5)] !bg-gradient-to-tr dark:from-primary-200 from-primary-100 via-base-bg to-main-bg border-solid border-gray-400/30 border-[0.5px] flex-auto`}
                  bodyClassName={`gap-6 flex flex-col relative overflow-hidden h-full !p-6`}
                  headerClassName={"!pb-6"}
                  removePaddings={true}
                >
                  <Image
                    src={`/base/image/money-hand-icon.png`}
                    alt={t("base-dashboard-promo:topup-rewards.title")}
                    width={160}
                    height={160}
                    className={`absolute right-0 bottom-0 mt-4 mr-4 dark:opacity-60`}
                  />
                  <h3
                    className={`font-montserrat text-text-2 text-2xl font-semibold mt-2`}
                  >
                    {t("base-dashboard-promo:referral.title")}
                  </h3>
                  <Link href={"/dashboard/referral"}>
                    <Button
                      type={"primary"}
                      className={`!shadow-none w-fit !font-extrabold !h-auto !py-2`}
                    >
                      {t("base-dashboard-promo:referral.continue")}
                    </Button>
                  </Link>
                </SimpleCard>
              </div>
            </div>

            <div className={`gap-6 flex col-span-full flex-col`}>
              <PagedItems<AvailablePromoCode>
                dependencies={[UserContext.user, refreshPromosState]}
                onChange={loadAvailablePromos}
                state={availablePromoState}
                bodyClassName={`grid lg:grid-cols-3 gap-6`}
                pageProps={{
                  pageSize: 5,
                  showSizeChanger: true,
                }}
                emptyItem={() => (
                  <div className={`h-full flex flex-col justify-center`}>
                    <div
                      className={`text-start text-gray-400 gap-4 flex items-center`}
                    >
                      <LuHandHeart size={`50px`} className={`text-text-2`} />
                      <div className={`flex flex-col gap-2.5`}>
                        <p className={`text-text-2 text-lg`}>
                          {t(
                            "base-dashboard-promo:possible-bonuses.card.empty"
                          )}
                        </p>
                        <p className={`max-w-[500px]`}>
                          {t(
                            "base-dashboard-promo:possible-bonuses.card.empty-description"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                renderItem={(promo, k) => (
                  <AvailablePromoCard key={k} promo={promo} />
                )}
              />
            </div>
          </div>
        </div>
      </DashboardLayoutEntry.Content>
      <DashboardLayoutEntry.RightSide className={`space-y-5`}>
        <NewsBoard />
      </DashboardLayoutEntry.RightSide>
    </DashboardLayoutEntry.Base>
  );
}

Page.getLayout = function getLayout(page: any) {
  return (
    <Layout>
      <DashboardLayout>
        <MetaBlock
          title={(t) => t("base-dashboard-promo:meta.title")}
          description={(t) => t("base-dashboard-promo:meta.description")}
        />

        {page}
      </DashboardLayout>
    </Layout>
  );
};
